import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, ArrowPathIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { fetchCases, updateCase } from '../api/client';
import Badge from './ui/Badge';
import { caseStatusTone } from './ui/badgeTones';
import { RefreshButton } from './ui/LoadingState';

/**
 * CaseDashboard - Landing page displaying all investigation cases in a table.
 *
 * Normalizes both PascalCase (mock data) and camelCase (real API) field names.
 */

function normalizeCase(c) {
  return {
    CaseID: c.CaseID || c.caseId || '',
    TenantID: c.TenantID || c.tenantId || '',
    TenantName: c.TenantName || c.tenantName || '',
    UPN: c.UPN || c.userPrincipalName || c.upn || '',
    Status: c.Status || c.status || '',
    CreatedAt: c.CreatedAt || c.createdAt || c.createdDate || '',
    LastRunDate: c.LastRunDate || c.lastRunDate || '',
    TicketNumber: c.TicketNumber || c.ticketNumber || '',
    Comment: c.Comment || c.comment || '',
  };
}

/**
 * Inline-editable comment cell for the case list. Post-investigation analyst
 * remark — click to edit, Enter (or blur) saves via updateCase, Esc cancels.
 * The parent <td> stops click propagation so editing doesn't open the case.
 */
function CommentCell({ caseId, value, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setDraft(value || ''); }, [value, editing]);

  const save = async () => {
    const next = draft.trim();
    if (next === (value || '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await updateCase(caseId, { Comment: next });
      onSaved(next);
      setEditing(false);
    } catch {
      setDraft(value || '');
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <textarea
        autoFocus
        rows={2}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
          if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
        }}
        className="input-field text-xs w-full min-w-[200px] resize-y"
        placeholder="Add a comment…"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-left w-full min-w-[200px] hover:bg-gray-800/40 rounded px-1.5 py-1 -mx-1.5 transition-colors"
      title={value || 'Add a comment'}
    >
      {value
        ? <span className="text-sm text-gray-300 line-clamp-2">{value}</span>
        : <span className="text-sm text-gray-600 italic">Add comment…</span>}
    </button>
  );
}

const STATUS_TABS = ['All', 'Open', 'InProgress', 'Closed'];
const STATUS_LABELS = { All: 'All', Open: 'Open', InProgress: 'In Progress', Closed: 'Closed' };

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

export default function CaseDashboard() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('InProgress');
  const [searchQuery, setSearchQuery] = useState('');

  const loadCases = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCases();
      const normalized = (Array.isArray(data) ? data : []).map(normalizeCase);
      setCases(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
  }, []);

  // Patch a single case's comment in local state after an inline save (avoids a full reload).
  const updateLocalComment = (caseId, comment) => {
    setCases((prev) => prev.map((c) => (c.CaseID === caseId ? { ...c, Comment: comment } : c)));
  };

  const filteredCases = useMemo(() => {
    let result = cases;

    // Filter by status tab
    if (activeTab !== 'All') {
      result = result.filter((c) => c.Status === activeTab);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          (c.CaseID && c.CaseID.toLowerCase().includes(q)) ||
          (c.UPN && c.UPN.toLowerCase().includes(q)) ||
          (c.TenantName && c.TenantName.toLowerCase().includes(q))
      );
    }

    return result;
  }, [cases, activeTab, searchQuery]);

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Investigation Cases</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage and monitor M365 security investigations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton
            loading={loading}
            onClick={loadCases}
            className="btn-secondary flex items-center gap-2"
            iconClassName="h-4 w-4"
            title="Refresh cases"
          />
          <Link to="/cases/new" className="btn-primary flex items-center gap-2">
            <PlusIcon className="h-4 w-4" />
            New Case
          </Link>
        </div>
      </div>

      {/* Filter Tabs and Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex gap-1 bg-gray-900 rounded-lg p-1 border border-gray-800">
          {STATUS_TABS.map((tab) => {
            const count =
              tab === 'All'
                ? cases.length
                : cases.filter((c) => c.Status === tab).length;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab
                    ? 'bg-gray-700 text-gray-100 shadow-sm'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {STATUS_LABELS[tab] || tab}
                <span className="ml-1.5 text-xs opacity-60">({count})</span>
              </button>
            );
          })}
        </div>

        <div className="relative">
          <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search cases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-9 w-full sm:w-64"
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm">
          <span className="font-medium">Error:</span> {error}
          <button
            onClick={loadCases}
            className="ml-3 underline hover:no-underline text-red-400"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                  Case ID
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                  Tenant
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                  UPN
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                  Status
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                  Created
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                  Last Run
                </th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">
                  Comment
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg
                        className="animate-spin h-8 w-8 text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading cases...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filteredCases.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <FunnelIcon className="h-10 w-10 text-gray-700" />
                      <div>
                        <p className="text-gray-400 font-medium">No cases found</p>
                        <p className="text-gray-600 text-sm mt-1">
                          {searchQuery
                            ? 'Try adjusting your search query.'
                            : 'Create a new investigation to get started.'}
                        </p>
                      </div>
                      {!searchQuery && (
                        <Link
                          to="/cases/new"
                          className="btn-primary mt-2 flex items-center gap-2 text-sm"
                        >
                          <PlusIcon className="h-4 w-4" />
                          New Case
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                filteredCases.map((caseItem) => (
                  <tr
                    key={caseItem.CaseID}
                    onClick={() => navigate(`/cases/${caseItem.CaseID}`)}
                    className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-sm font-mono text-blue-400">
                      {caseItem.CaseID}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {caseItem.TenantName || caseItem.TenantID}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {caseItem.UPN}
                    </td>
                    <td className="px-6 py-4">
                      <Badge tone={caseStatusTone(caseItem.Status)} dot>
                        {STATUS_LABELS[caseItem.Status] || caseItem.Status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {formatDate(caseItem.CreatedAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">
                      {formatDate(caseItem.LastRunDate)}
                    </td>
                    <td
                      className="px-6 py-4 align-top max-w-xs"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CommentCell
                        caseId={caseItem.CaseID}
                        value={caseItem.Comment}
                        onSaved={(v) => updateLocalComment(caseItem.CaseID, v)}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Table Footer */}
        {!loading && filteredCases.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-800 bg-gray-900/50">
            <p className="text-xs text-gray-500">
              Showing {filteredCases.length} of {cases.length} case
              {cases.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
